import { ArrowDown, ArrowUp, ArrowUpDown, ListFilter } from 'lucide-react'
import { type CSSProperties, type ReactNode, useState } from 'react'
import { createPortal } from 'react-dom'
import { useViewportBand } from '../lib/viewport'
import { Button } from './Button'
import { Caret, foldLabel } from './Caret'
import { Figure } from './Figure'
import { InfoTip } from './InfoTip'
import { Notice } from './index'
import { Menu, type MenuItem } from './Menu'
import { opensRecord } from './opensRecord'
import { usePageToolSlot } from './PageAction'
import { TickMark } from './TickMark'
import { layOut } from './tree-rows'
import './table.css'
import { OverflowGroup } from './OverflowGroup'
import { SearchField } from './SearchField'

/**
 * The header that sits on top of a table or list — the app's one list header.
 *
 * ## Slots
 *
 * A screen fills slots; it never lays anything out. Each slot says what belongs in
 * it, and what a screen puts there is the only decision it makes:
 *
 * | Slot | What fills it |
 * |---|---|
 * | `search` / `onSearch` | free-text over the list. Always the first tool. |
 * | `filters` | the `Select`s that narrow it |
 * | `view` | the `SegmentedControl` between readings of the same set (table ⇄ cards) |
 * | `kpi` | the figure the list is read for — owed, received — just before the view |
 * | `meta` | any other fact that is not the count — how many need review |
 * | `info` | the ⓘ prose: why this list exists |
 *
 * **Making a new record is not a slot here.** It acts on the section rather than on
 * the list, so it has to survive the list becoming a board or a set of cards, and
 * it is the screen's one confident mark — which belongs beside the page's name, not
 * at the end of a row of filters. Screens declare it with `PageCreate`, and the
 * shell draws it in the header.
 *
 * ## What the slots mean on a phone
 *
 * **The mobile rendering is derived from which slots are filled** — it is not a
 * second layout somebody maintains in parallel. The strip becomes the shell's one
 * row of chrome, as a set of equal hairline-divided cells:
 *
 * - `onSearch` → a search cell, which expands over the row when you open it
 * - `filters` / `view` / `kpi` / `meta` → one cell, opening them on a panel with room
 * - a declared `PageCreate` → the actions cell, drawn by the shell
 *
 * A slot nobody filled produces no cell, so the row is two thirds on a list with
 * no filters and three on one with them. Nothing is tuned to a count, and no page
 * carries an entry point that opens onto nothing.
 *
 * The strip moves by **portal**, not by the shell copying nodes out of a context:
 * the search keeps its own state where it is declared, so typing in it does not
 * travel through the shell and back.
 */
export function TableHeader({
  search,
  onSearch,
  searchScope,
  count,
  noun,
  plural,
  filters,
  view,
  kpi,
  meta,
  info,
}: {
  search?: string
  onSearch?: (value: string) => void
  /**
   * What the search covers, shown INSIDE the field.
   *
   * For a list that searches more than one thing. It belongs in the field
   * because a control elsewhere in the row cannot say it qualifies this one —
   * the reader would have to notice it and hold the connection.
   */
  searchScope?: ReactNode
  /**
   * How many rows the list is SHOWING — after its filters and its search, not
   * how many exist — and what they are: "3 households".
   *
   * It composes the search field's placeholder, and it is the ONE place the
   * phrasing is decided: written per screen it came out as "Search rules…" here
   * and "Search households and charges…" there, so the same control read
   * differently on every page and one of them was quietly a list of which
   * FIELDS get searched — a promise that goes stale the moment a page searches
   * one more.
   *
   * The count belongs here rather than as a fact at the end of the strip: it is
   * the same number, and the search field is where the eye already is when it
   * wants to know how many there are to search.
   */
  count?: number
  /** Singular — "household". Pluralised by adding s unless `plural` says otherwise. */
  noun?: string
  plural?: string
  filters?: ReactNode
  view?: ReactNode
  /**
   * The one figure the list is read for, and what it is: `$1,240.00 owed`, on one
   * line. A compact `Figure` at the start of the trailing tools, so the
   * amount leads the view switch rather than trailing it as a sentence.
   */
  kpi?: { amount: string; label: ReactNode }
  meta?: ReactNode
  /** An ⓘ before the search — the "why this exists" prose, out of the header. */
  info?: ReactNode
}) {
  /*
   * One of the few honest uses of the band hook (see `lib/viewport`): this is not
   * "move something" — CSS cannot move a node into a different container, and a
   * second copy of a search field would be a second piece of state.
   */
  const band = useViewportBand()
  const slot = usePageToolSlot()
  const inRow = band === 'handheld' && slot !== null
  /*
   * Which cells the row has is read off the slots, so an unfilled slot is simply
   * absent rather than an entry point that opens onto nothing — a "Filter" cell on
   * a list with nothing to filter is a control that teaches you it does nothing.
   *
   * In the row the test is stricter: a **control**, not a fact. The count alone
   * earned a cell on the campaigns list, which put a sentence ("1 campaign") in a
   * row of buttons, reading as a control that does nothing when tapped. The
   * priority order puts facts about a list behind the list itself, and the count
   * still travels inside the panel wherever there are controls to open.
   */
  /*
   * "Search 3 households…" — the count and the noun, and nothing about which
   * fields are matched. A page searching a household's name, its address and
   * the charges under it cannot say so in a placeholder without writing a
   * sentence, and would have to rewrite it every time the search widens.
   */
  const searchPlaceholder =
    count !== undefined && noun
      ? `Search ${count} ${count === 1 ? noun : (plural ?? `${noun}s`)}…`
      : 'Search…'

  const hasTools = Boolean(filters || view || kpi || meta)
  const hasRowTools = Boolean(filters || view)

  /* The figure the list is read for and the switch between its views. Beside
     the page they stay on the strip whatever the filters do — a total hidden
     behind a funnel is a total nobody reads. In the phone's one row they go
     into the panel with everything else. */
  const end =
    kpi || view ? (
      <div className="table-header__end">
        {kpi ? <Figure amount={kpi.amount} label={kpi.label} compact /> : null}
        {view}
      </div>
    ) : null

  const strip = (
    <div className="table-header">
      {/* The page's blurb is not one of the row's thirds. In the row every item is
          a target you aim at, and a ⓘ is a paragraph you read — it would take a
          third of the width to explain a screen you are already looking at. */}
      {info && !inRow ? <InfoTip>{info}</InfoTip> : null}
      {onSearch ? (
        /* In the row it is a button like its neighbors, and opening it expands the
           field over the row — a third of 375px holds a magnifier and a clipped
           placeholder, which is a field that cannot say what it searches. */
        <SearchField
          value={search ?? ''}
          onChange={onSearch}
          placeholder={searchPlaceholder ?? 'Search…'}
          collapsed={inRow}
          scope={searchScope}
        />
      ) : null}
      {/*
        Everything but the search collapses into one entry point where the strip is
        narrow, so it stays one row: search, and a way to everything else.

        Search stays out of it because it is the first tool and the one you
        reach for without looking. The rest — the filters, the view switch and the
        count — are reached for by name, which is what a popover is good at.
        Wrapping into three rows, which is what this did before, put a story of
        chrome above every list and grew a fourth the moment a filter was added.
      */}
      {/* A funnel and a word, not the anonymous ⋯: on a phone this entry point sits
          beside the page's actions, and two identical dots are two things you have
          to open to tell them apart. It filters a list — it is not a settings
          panel, so it does not wear settings' sliders. */}
      {(inRow ? hasRowTools : hasTools) ? (
        <OverflowGroup
          label="Filters and view"
          short="Filter"
          icon={ListFilter}
          align="end"
          surface="panel"
          forceCollapsed={inRow}
          /* The filters fold first, behind the funnel, and the total and the
             view switch stay on the strip; only where those do not fit either
             does the whole strip become the one entry point. What `meta`
             says, a fact rather than a control, folds with the filters. */
          fold={
            filters || meta ? (
              <>
                {filters ? (
                  <div className="table-header__filters">{filters}</div>
                ) : null}
                {meta ? (
                  <span className="table-header__meta">{meta}</span>
                ) : null}
              </>
            ) : undefined
          }
        >
          {end}
        </OverflowGroup>
      ) : null}
    </div>
  )

  /*
   * On a phone the strip is not here — it is the leading half of the shell's one
   * row, beside the page's actions. Until the slot exists (the first render, or a
   * surface with no shell) it stays where it is declared, so a list is never left
   * without its search.
   */
  return band === 'handheld' && slot ? createPortal(strip, slot) : strip
}

/**
 * A card that holds a list or table — the surface, border and radius are the
 * card's, so tables don't each restyle their frame. Compose a TableHeader as
 * `header`.
 *
 * `compact` is the same card at a denser size, for a table shown inside a panel
 * rather than on a page: tighter cells, and no 46rem floor. That floor is right
 * on a page — a table that compresses past readable is worse than one you
 * scroll — but a table the assistant produced is a handful of columns it chose
 * to fit the answer, and holding it to a page table's width would make every
 * one of them scroll sideways in a 400px panel. The variant is a prop rather
 * than a second component, so there is still one table frame in the app.
 */
export function TableCard({
  header,
  footer,
  compact,
  children,
}: {
  header?: ReactNode
  footer?: ReactNode
  compact?: boolean
  children: ReactNode
}) {
  return (
    <section className={`table-card${compact ? ' table-card--compact' : ''}`}>
      {header ? <div className="table-card__header">{header}</div> : null}
      <div className="table-card__body">{children}</div>
      {footer ? <div className="table-card__footer">{footer}</div> : null}
    </section>
  )
}

/**
 * One column of a table: what it is called, and what it renders from a row.
 *
 * The cell is a function of the row rather than markup a screen writes, which is
 * what lets the frame own the grid, the tree's indent and the row's menu — a
 * table handed finished `<tr>`s can own none of them.
 */
export type Column<Row> = {
  /** Stable per column, and the cell's key. */
  key: string
  /** Sentence case, muted by the frame. The actions column names nothing. */
  header?: ReactNode
  /** The server-owned ordering this header controls. */
  sortKey?: string
  cell: (row: Row) => ReactNode
  /**
   * Figures align on their trailing edge, which is what makes a column of them
   * comparable down the page, and they do not wrap — a wrapped amount reads as
   * two.
   */
  align?: 'end'
  /** The cell's own face: the numeric family, a muted second line. */
  className?: string
  /** The column's width, which the header states and the cell does not. */
  headClassName?: string
}

export type TablePaging = {
  start: number
  end: number
  total: number
  hasPrevious: boolean
  hasNext: boolean
  busy?: boolean
  onPrevious: () => void
  onNext: () => void
}

/** The one paging footer, shared by the table and its card reading. */
export function TablePagination({ paging }: { paging: TablePaging }) {
  return (
    <div className="table__paging">
      <span className="table__range">
        {paging.total === 0
          ? '0 records'
          : `${paging.start}–${paging.end} of ${paging.total}`}
      </span>
      <div className="table__paging-actions">
        <Button
          disabled={paging.busy || !paging.hasPrevious}
          onClick={paging.onPrevious}
          variant="secondary"
        >
          Previous
        </Button>
        <Button
          disabled={paging.busy || !paging.hasNext}
          onClick={paging.onNext}
          variant="secondary"
        >
          Next
        </Button>
      </div>
    </div>
  )
}

/**
 * A listing: the columns and the rows, and the frame owns everything else.
 *
 * A screen supplies data and never writes `<table>`, a row loop, a trailing menu
 * or an empty state — which is what keeps the grid, the sticky header, the
 * sideways scroll and the way a row opens identical wherever a list appears. A
 * screen writing its own is a chance to rule the row above instead of below, and
 * a twist written inline is a twist the next listing cannot reach.
 *
 * A column may be `false` where the data does not fill it. Whether a shelf has a
 * key or a roster a balance is the SCREEN's to answer, being a fact about the
 * rows — and a column of blanks is width taken from the ones that say something.
 */
export function Table<Row>({
  columns,
  rows,
  rowKey,
  name,
  onOpen,
  canOpen,
  actions,
  rowClass,
  parentOf,
  folded,
  defaultFolded,
  onFoldedChange,
  empty,
  header,
  sort,
  paging,
  compact,
  pick,
}: {
  columns: (Column<Row> | false | null | undefined)[]
  rows: Row[]
  /** The row's identity — its id, never its position. */
  rowKey: (row: Row) => string
  /**
   * What the row is called. The frame composes from it what a screen reader is
   * told — "Open the Katz household", "Show what is under Bereishis" — so a
   * column of identical "Open" labels cannot happen.
   */
  name?: (row: Row) => string
  /**
   * What clicking the row does. Given one, the row says so and answers the
   * keyboard as well as the pointer (`opensRecord`).
   */
  onOpen?: (row: Row) => void
  /**
   * Which rows open. A row that heads others — a fund over its items — is a
   * heading and not a record, so it is drawn as one rather than as a control
   * that announces an opening and performs none. Every row opens where this
   * is not given.
   */
  canOpen?: (row: Row) => boolean
  /**
   * The row's actions, as a trailing `Menu` — never buttons in a cell. A row
   * with none, one nobody here owns, answers null and its cell stays empty.
   */
  actions?: (row: Row) => MenuItem[] | null
  /** Where a row's own facts need a face — a roster row that centers its cells. */
  rowClass?: (row: Row) => string | undefined
  /**
   * Which row this one sits under, which makes the listing a tree: the frame
   * orders the rows by it, indents them and folds them. A parent the list does
   * not carry is a root, so narrowing a tree cannot strand what is left.
   */
  parentOf?: (row: Row) => string | null
  /**
   * Which branches are SHUT, by row key, where the screen keeps that itself —
   * a listing a reader leaves for a record and returns to, whose folds are
   * theirs to find as they left them. Held here where none is given, and then
   * it lasts as long as the listing is on screen.
   */
  folded?: ReadonlySet<string>
  /** What is shut to begin with, where the frame holds the folds. */
  defaultFolded?: ReadonlySet<string>
  onFoldedChange?: (folded: ReadonlySet<string>) => void
  /** What to say where there is nothing to show. Nothing said renders nothing. */
  empty?: ReactNode
  /** A `TableHeader`, framed by the card. */
  header?: ReactNode
  /**
   * Which column orders the rows. A column cycles ascending, descending, then
   * back to the list's own order — `key: null` — so a reader who sorted can
   * always get the list back as it came.
   */
  sort?: {
    key: string | null
    direction: 'asc' | 'desc'
    onChange: (key: string | null, direction: 'asc' | 'desc') => void
  }
  paging?: TablePaging
  compact?: boolean
  /**
   * Rows the reader ticks to act on together — a leading column of square
   * marks, one per row, keyed by `rowKey`. The screen holds which are ticked
   * and the act they feed; the frame draws the marks and answers the keyboard.
   * A row `canPick` refuses draws no mark: it is listed to be read, and the
   * reason it cannot be chosen is the screen's to state in the row.
   */
  pick?: {
    picked: ReadonlySet<string>
    onPickedChange: (picked: ReadonlySet<string>) => void
    canPick?: (row: Row) => boolean
    /** What ticking this row does, distinct from every other tick on the
     *  screen — "Correct the name of Miriam Feldblum". */
    label: (row: Row) => string
    /** What ticking every row does. Given, the column's head ticks or clears
     *  every row that can be picked, and says when some are and some not. */
    all?: string
  }
}) {
  /* Branches are open, and what is held is what somebody SHUT: a listing shows
     its rows, so the state worth keeping is the exception to that. */
  const [held, setHeld] = useState<ReadonlySet<string>>(
    () => defaultFolded ?? new Set(),
  )
  const shut = folded ?? held
  const cols = columns.filter((c): c is Column<Row> => Boolean(c))
  const laid = layOut(rows, rowKey, parentOf, shut)
  const laidWithActions = laid.map((entry) => ({
    ...entry,
    items: actions?.(entry.row) ?? null,
  }))
  const hasActions = laidWithActions.some(({ items }) => Boolean(items?.length))

  const fold = (key: string) => {
    const next = new Set(shut)
    if (!next.delete(key)) next.add(key)
    /* Held here only where nobody outside holds it: two copies of one answer
       are two that can disagree. */
    if (folded === undefined) setHeld(next)
    onFoldedChange?.(next)
  }

  return (
    <TableCard
      header={header}
      footer={paging ? <TablePagination paging={paging} /> : undefined}
      compact={compact}
    >
      {/* No head over no rows: column names above a sentence saying there is
          nothing to put under them is chrome explaining an empty room. */}
      {laid.length === 0 ? (
        empty ? (
          <Notice>{empty}</Notice>
        ) : null
      ) : (
        <table>
          {/* A head is drawn where the columns are NAMED. A list whose rows say
              what they hold as they go — a name against its amount — would
              otherwise carry a heavy rule under nothing. */}
          {cols.some((c) => c.header !== undefined) ? (
            <thead>
              <tr>
                {pick ? (
                  <th className="table__pick">
                    {pick.all ? (
                      <PickAll
                        rows={laid.map((l) => l.row)}
                        rowKey={rowKey}
                        pick={pick}
                      />
                    ) : null}
                  </th>
                ) : null}
                {cols.map((c) => (
                  <th
                    key={c.key}
                    className={c.headClassName}
                    data-align={c.align}
                    aria-sort={
                      c.sortKey
                        ? sort?.key === c.sortKey
                          ? sort.direction === 'asc'
                            ? 'ascending'
                            : 'descending'
                          : 'none'
                        : undefined
                    }
                  >
                    {c.sortKey && sort ? (
                      <button
                        type="button"
                        className="table__sort"
                        onClick={() =>
                          sort.key !== c.sortKey
                            ? sort.onChange(c.sortKey ?? c.key, 'asc')
                            : sort.direction === 'asc'
                              ? sort.onChange(c.sortKey, 'desc')
                              : sort.onChange(null, 'asc')
                        }
                      >
                        <span>{c.header}</span>
                        {sort.key === c.sortKey ? (
                          sort.direction === 'asc' ? (
                            <ArrowUp aria-hidden="true" />
                          ) : (
                            <ArrowDown aria-hidden="true" />
                          )
                        ) : (
                          <ArrowUpDown aria-hidden="true" />
                        )}
                      </button>
                    ) : (
                      c.header
                    )}
                  </th>
                ))}
                {hasActions ? <th className="table__menu" /> : null}
              </tr>
            </thead>
          ) : null}
          <tbody>
            {laidWithActions.map(({ row, key, depth, branch, items }) => {
              const label = name?.(row)
              return (
                <tr
                  key={key}
                  className={rowClass?.(row)}
                  {...(onOpen && (canOpen?.(row) ?? true)
                    ? {
                        'data-open': '',
                        ...opensRecord({
                          onOpen: () => onOpen(row),
                          label: `Open ${label ?? ''}`.trim(),
                        }),
                      }
                    : {})}
                >
                  {pick ? (
                    <td className="table__pick" data-no-open>
                      {(pick.canPick?.(row) ?? true) ? (
                        <TickMark
                          checked={pick.picked.has(key)}
                          label={pick.label(row)}
                          onChange={(on) => {
                            const next = new Set(pick.picked)
                            if (on) next.add(key)
                            else next.delete(key)
                            pick.onPickedChange(next)
                          }}
                        />
                      ) : null}
                    </td>
                  ) : null}
                  {cols.map((c, at) => (
                    <td
                      key={c.key}
                      className={c.className}
                      data-align={c.align}
                    >
                      {/* The fold belongs to the row's first column, beside the
                          name it opens — a caret further along the row marks a
                          branch from somewhere the eye is not. */}
                      {at === 0 && parentOf ? (
                        <span
                          className="table__branch"
                          /* The depth is a fact about this row, so it rides on
                             the element; what a level costs stays in the sheet. */
                          style={{ '--depth': depth } as CSSProperties}
                        >
                          {branch ? (
                            <Caret
                              expanded={!shut.has(key)}
                              label={foldLabel(shut.has(key), label ?? 'this')}
                              onToggle={() => fold(key)}
                            />
                          ) : (
                            /* A leaf keeps the caret's width, so every name in
                               the column starts at its own level's edge. */
                            <span className="table__nofold" />
                          )}
                          {c.cell(row)}
                        </span>
                      ) : (
                        c.cell(row)
                      )}
                    </td>
                  ))}
                  {hasActions ? (
                    <td className="table__menu" data-no-open>
                      {items ? (
                        <Menu
                          ariaLabel={`What to do with ${label ?? 'this row'}`}
                          items={items}
                        />
                      ) : null}
                    </td>
                  ) : null}
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </TableCard>
  )
}

/** The head's tick: every row that can be picked, ticked or cleared at once. */
function PickAll<Row>({
  rows,
  rowKey,
  pick,
}: {
  rows: Row[]
  rowKey: (row: Row) => string
  pick: NonNullable<Parameters<typeof Table<Row>>[0]['pick']>
}) {
  const keys = rows.filter((row) => pick.canPick?.(row) ?? true).map(rowKey)
  const ticked = keys.filter((key) => pick.picked.has(key)).length
  const every = keys.length > 0 && ticked === keys.length
  return (
    <TickMark
      checked={every}
      mixed={ticked > 0 && !every}
      disabled={keys.length === 0}
      label={pick.all ?? ''}
      onChange={() => {
        const next = new Set(pick.picked)
        for (const key of keys) {
          if (every) next.delete(key)
          else next.add(key)
        }
        pick.onPickedChange(next)
      }}
    />
  )
}
